import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

const UserSelectionContext = createContext();

export const UserSelectionProvider = ({ children }) => {
  // Card click state - tracks which card was clicked (status-based cards)
  const [selectedCard, setSelectedCard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  
  // Filters state - matching the backend expectations
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    phno: "",
    role: "",
    status: "" // This maps to isActive parameter for active/inactive
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalUsers: 0
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Store filtered users for each card combination
  const [filteredUsersByCard, setFilteredUsersByCard] = useState({});
  
  const prevCardKeyRef = useRef('');
  const lastUpdateTimeRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerUserRefresh = useCallback(() => {
      setRefreshKey(prevKey => prevKey + 1);
    }, []);
  
  
  const currentCardKey = useMemo(() => {
    const cardKey = selectedCard || 'all';
    if (prevCardKeyRef.current !== cardKey) {
      prevCardKeyRef.current = cardKey;
    }
    return cardKey;
  }, [selectedCard]);
  
  const filteredUsers = useMemo(() => {
    return filteredUsersByCard[currentCardKey] || [];
  }, [filteredUsersByCard, currentCardKey]);
  
  const setFilteredUsers = useCallback((users) => {
    const now = Date.now();
    
    // Throttling to prevent excessive updates
    if (now - lastUpdateTimeRef.current < 50) {
      return;
    }
    lastUpdateTimeRef.current = now;
    
    setFilteredUsersByCard(prev => {
      const currentUsers = prev[currentCardKey];
      
      // Always update if users is empty array (important for clearing)
      if (Array.isArray(users) && users.length === 0) {
        return {
          ...prev,
          [currentCardKey]: []
        };
      }
      
      // Check if users are actually different
      if (currentUsers === users) {
        return prev;
      }
      
      if (currentUsers && currentUsers.length === users.length) {
        let hasChanged = false;
        for (let i = 0; i < users.length; i++) {
          if (currentUsers[i] !== users[i]) {
            hasChanged = true;
            break;
          }
        }
        if (!hasChanged) {
          return prev;
        }
      }
      
      return {
        ...prev,
        [currentCardKey]: Array.isArray(users) ? [...users] : []
      };
    });
  }, [currentCardKey]);
  

  const buildQueryParams = useCallback(() => {
  const params = new URLSearchParams();
  
  // Pagination - skip if "all" is selected
  if (pagination.limit !== "all") {
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());
  }
  
  // Search term
  if (searchTerm && searchTerm.trim()) {
    params.set('search', searchTerm.trim());
  }
  
  // Card-based filtering - Map cards to correct API parameters and exclude "all"
  if (selectedCard && selectedCard !== "all") {
    if (selectedCard === 'active') {
      params.set('status', 'active');
    } else if (selectedCard === 'inactive') {
      params.set('status', 'inActive');
    }
    // Keep onClick for backend compatibility
    params.set('onClick', selectedCard);
  }
  
  // Apply all filters with correct parameter names for user API and exclude "all" values
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value.trim() && value.trim() !== "all") {
      // Map context filter names to API parameter names
      if (key === 'status') {
        // FIXED: Use 'status' parameter instead of 'isActive'
        if (value === 'active') {
          params.set('status', 'active');
        } else if (value === 'inactive') {
          params.set('status', 'inActive');
        }
      } else {
        params.set(key, value.trim());
      }
    }
  });
  
  return params.toString();
}, [pagination, searchTerm, selectedCard, filters]);
  
  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      hasMore: true
    }));
  }, []);
  
  // Clear all filters and reset state
  const clearAllFilters = useCallback(() => {
    
    setFilters({
      name: "",
      email: "",
      phno: "",
      role: "",
      status: ""
    });
    
    setSearchTerm('');
    setSelectedCard(null);
    resetPagination();
    
    // Clear the filtered users for current card
    setFilteredUsersByCard(prev => ({
      ...prev,
      [currentCardKey]: []
    }));
    
  }, [resetPagination, currentCardKey]);
  

    const handleCardClick = useCallback((cardType) => {
      setIsLoading(true); // Start loading
    
      // If the "Total Cases" card is clicked, reset EVERYTHING.
      if (cardType === "" || cardType === "total" || cardType === null) {
        clearAllFilters(); 
      } else {
        // For any other card, just toggle its state as before.
        setSelectedCard(prevCard => prevCard === cardType ? null : cardType);
        resetPagination();
      }
    }, [clearAllFilters, resetPagination]);
  
  // Stable setters
  const setFiltersStable = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const hasChanged = Object.keys(newFilters).some(key => 
        newFilters[key] !== prevFilters[key]
      );
      
      if (hasChanged) {
        resetPagination();
        return newFilters;
      }
      
      return prevFilters;
    });
  }, [resetPagination]);
  
  const setSearchTermStable = useCallback((term) => {
    if (term !== searchTerm) {
      setSearchTerm(term);
      resetPagination();
    }
  }, [searchTerm, resetPagination]);
  
  // Check if any filters are active - FIXED: More comprehensive check
  const hasActiveFilters = useMemo(() => {
    const hasCard = selectedCard !== null;
    const hasSearch = searchTerm.trim() !== '';
    const hasFilters = Object.values(filters).some(value => value && value.trim() !== '');
    
    
    return hasCard || hasSearch || hasFilters;
  }, [selectedCard, searchTerm, filters]);
  
  // Update pagination info
  const updatePaginationInfo = useCallback((totalUsers, hasMore) => {
    setPagination(prev => ({
      ...prev,
      totalUsers,
      hasMore
    }));
  }, []);
  
  // Load more users (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  }, [pagination.hasMore]);
  
  const contextValue = useMemo(() => ({
    // Card selection
    selectedCard,
    setSelectedCard,
    handleCardClick,
    
    // Filters
    filters,
    setFilters: setFiltersStable,
    
    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,
    
    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    
    // Filtered users
    filteredUsers,
    setFilteredUsers,

    refreshKey,
    triggerUserRefresh,
    
    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    currentCardKey,
    isLoading,
    setIsLoading,
  }), [
    selectedCard,
    handleCardClick,
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredUsers,
    setFilteredUsers,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    currentCardKey,
    refreshKey,
    triggerUserRefresh,
    isLoading,
    setIsLoading,
  ]);
  
  return (
    <UserSelectionContext.Provider value={contextValue}>
      {children}
    </UserSelectionContext.Provider>
  );
};

export const useUserSelection = () => {
  const context = useContext(UserSelectionContext);
  if (!context) {
    throw new Error('useUserSelection must be used within an UserSelectionProvider');
  }
  return context;
};